import { useEffect, useState } from 'react'
import { api } from '../api/client.ts'
import { Avatar } from './Avatar.tsx'
import { Icon } from './Icon.tsx'
import { plural } from '../lib/format.ts'

/**
 * КОМУ ОТКРЫТ ОБЪЕКТ
 *
 * Одна панель на все разделы. Открыть можно кругу («Все», «Родители»)
 * или человеку поимённо.
 *
 * Круг удобнее: выдал один раз — и новый член семьи, попав в круг,
 * получит доступ сам. Поимённо нужно там, где круга много: дневник
 * здоровья открывают одному супругу, а не всем сразу.
 */

type Share = {
  id: string
  permission: 'READ' | 'WRITE' | 'ADMIN'
  circle: { id: string; name: string } | null
  user: { id: string; displayName: string; avatarUrl: string | null } | null
}

type Data = {
  shares: Share[]
  circles: { id: string; name: string; isDefault: boolean; size: number }[]
  people: { id: string; displayName: string; avatarUrl: string | null }[]
}

const LEVELS: Record<string, string> = {
  READ: 'только смотрит',
  WRITE: 'может менять',
  ADMIN: 'полные права',
}

export function SharePanel({
  entityType,
  entityId,
  onClose,
  title = 'Кому открыт доступ',
}: {
  entityType: string
  entityId: string
  onClose: () => void
  title?: string
}) {
  const [data, setData] = useState<Data | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [level, setLevel] = useState<'READ' | 'WRITE'>('WRITE')

  const load = () => {
    api<Data>(`/api/shares/${entityType}/${entityId}`)
      .then(setData)
      .catch((e) => setError(e instanceof Error ? e.message : 'Не удалось загрузить'))
  }

  useEffect(load, [entityType, entityId])

  const grant = async (body: { circleId?: string; userId?: string }) => {
    setBusy(true)
    setError(null)
    try {
      await api(`/api/shares/${entityType}/${entityId}`, {
        method: 'POST',
        body: { ...body, permission: level },
      })
      load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не получилось')
    } finally {
      setBusy(false)
    }
  }

  const revoke = async (shareId: string) => {
    setBusy(true)
    try {
      await api(`/api/shares/${shareId}`, { method: 'DELETE' })
      load()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не получилось')
    } finally {
      setBusy(false)
    }
  }

  const sharedCircleIds = new Set(data?.shares.map((s) => s.circle?.id).filter(Boolean))
  const sharedUserIds = new Set(data?.shares.map((s) => s.user?.id).filter(Boolean))

  return (
    <div className="sheet" onClick={onClose}>
      <div className="sheet-body" onClick={(e) => e.stopPropagation()}>
        <div className="sheet-head">
          <h2>{title}</h2>
          <button className="icon-btn" onClick={onClose} aria-label="Закрыть">
            ✕
          </button>
        </div>

        {!data && !error && (
          <div className="center-screen" style={{ height: 100 }}>
            <div className="spinner" />
          </div>
        )}

        {data && (
          <>
            {data.shares.length === 0 ? (
              <p className="sheet-text">Пока видишь только ты.</p>
            ) : (
              <div className="member-list">
                {data.shares.map((share) => (
                  <div key={share.id} className="member-row">
                    {share.user ? (
                      <Avatar
                        id={share.user.id}
                        name={share.user.displayName}
                        url={share.user.avatarUrl}
                        size={36}
                      />
                    ) : (
                      <span className="circle-badge" aria-hidden="true">
                        <Icon name="family" size={21} />
                      </span>
                    )}
                    <div className="member-text">
                      <div className="member-name">
                        {share.user?.displayName ?? share.circle?.name}
                      </div>
                      <div className="member-sub">{LEVELS[share.permission]}</div>
                    </div>
                    <button
                      className="member-remove"
                      onClick={() => void revoke(share.id)}
                      disabled={busy}
                      title="Закрыть доступ"
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}

            <h3 className="sheet-sub">Открыть ещё</h3>

            <div className="level-switch">
              <button
                className={'level-btn' + (level === 'WRITE' ? ' is-active' : '')}
                onClick={() => setLevel('WRITE')}
              >
                Может менять
              </button>
              <button
                className={'level-btn' + (level === 'READ' ? ' is-active' : '')}
                onClick={() => setLevel('READ')}
              >
                Только смотрит
              </button>
            </div>

            <div className="member-list">
              {data.circles
                .filter((c) => !sharedCircleIds.has(c.id))
                .map((circle) => (
                  <button
                    key={circle.id}
                    className="member-row pick"
                    onClick={() => void grant({ circleId: circle.id })}
                    disabled={busy}
                  >
                    <span className="circle-badge" aria-hidden="true">
                      <Icon name="family" size={21} />
                    </span>
                    <div className="member-text">
                      <div className="member-name">{circle.name}</div>
                      <div className="member-sub">
                        {circle.size} {plural(circle.size, 'человек', 'человека', 'человек')}
                      </div>
                    </div>
                  </button>
                ))}

              {data.people
                .filter((p) => !sharedUserIds.has(p.id))
                .map((person) => (
                  <button
                    key={person.id}
                    className="member-row pick"
                    onClick={() => void grant({ userId: person.id })}
                    disabled={busy}
                  >
                    <Avatar
                      id={person.id}
                      name={person.displayName}
                      url={person.avatarUrl}
                      size={36}
                    />
                    <div className="member-text">
                      <div className="member-name">{person.displayName}</div>
                    </div>
                  </button>
                ))}
            </div>
          </>
        )}

        {error && <div className="sheet-error">{error}</div>}
      </div>
    </div>
  )
}
